import React from 'react';
import {
    Zap,
    BarChart3,
    LineChart,
    Dumbbell,
    Ruler,
    CalendarRange,
} from 'lucide-react';
import { LANDING } from '@/constants/testIds';
import PhoneMockup from './PhoneMockup';

const ART = {
    dashboard:
        'https://customer-assets.emergentagent.com/job_hypertroop-fit/artifacts/u9nfc8e6_Dashboard.png',
    progress:
        'https://customer-assets.emergentagent.com/job_hypertroop-fit/artifacts/aeenjb2n_Progress.png',
    exerciseList:
        'https://customer-assets.emergentagent.com/job_hypertroop-fit/artifacts/8302ea8v_Exercise.png',
    analytics:
        'https://customer-assets.emergentagent.com/job_hypertroop-fit/artifacts/hgk9zwzx_Analytics.png',
    measurements:
        'https://customer-assets.emergentagent.com/job_hypertroop-fit/artifacts/rv54gqju_Measurements.png',
};

const MUSCLE_TAGS = [
    { label: 'QUADS', color: '#1ec8e0' },
    { label: 'HAMSTRING', color: '#ff9c2a' },
    { label: 'GLUTES', color: '#86d959' },
    { label: 'CALFS', color: '#22c8b4' },
    { label: 'ABS', color: '#9b6cff' },
    { label: 'CHEST', color: '#ef4444' },
    { label: 'BACK', color: '#3b82f6' },
    { label: 'SHOULDER', color: '#fbbf24' },
    { label: 'FOREARMS', color: '#ec4899' },
];

const features = [
    {
        Icon: Zap,
        eyebrow: '01 — Dashboard',
        headline: 'Your fitness at a glance',
        body:
            "Start every day with a clear picture: sessions this week, total volume, body weight trend, activity streak, and your latest personal records — all on one screen.",
        bullets: ['Sessions this week', 'Volume trend', 'Activity streak'],
        mockup: <PhoneMockup src={ART.dashboard} alt="Dashboard" />,
    },
    {
        Icon: BarChart3,
        eyebrow: '02 — Progress Logging',
        headline: 'Log every set. See every gain.',
        body:
            "Track weight and reps set-by-set for each exercise. A side-by-side history table shows your past sessions so you can see exactly how you're progressing.",
        bullets: ['Set-by-set logging', 'Past sessions side-by-side', 'Save progress in one tap'],
        mockup: <PhoneMockup src={ART.progress} alt="Progress Logging" />,
    },
    {
        Icon: LineChart,
        eyebrow: '03 — Analytics',
        headline: 'Data that shows the full picture',
        body:
            'Visualise your volume over time and zoom into 7D, 30D, 90D, or All to spot trends, imbalances, and breakthroughs.',
        bullets: ['Volume over time', 'Muscle-group breakdown', 'Weekly summary'],
        mockup: <PhoneMockup src={ART.analytics} alt="Analytics" />,
    },
    {
        Icon: Dumbbell,
        eyebrow: '04 — Exercise Library',
        headline: '832+ exercises, curated & ready',
        body:
            'Browse, search, and filter a library of over 832 exercises by type, muscle group, and equipment. Watch video demos directly in the app. Build your own custom exercises.',
        bullets: ['Filter by muscle group', 'Video tutorials inline', 'Custom exercises'],
        mockup: <PhoneMockup src={ART.exerciseList} alt="Exercise Library" />,
        showTags: true,
    },
    {
        Icon: Ruler,
        eyebrow: '05 — Measurements',
        headline: 'Track your whole body, not just the bar',
        body:
            'Log body weight and 12 circumference measurements — neck, chest, biceps, forearms, waist, hips, thighs, calves. View trend charts across any time range.',
        bullets: ['12 circumferences', 'Log & History tabs', 'Weekly check-ins'],
        mockup: <PhoneMockup src={ART.measurements} alt="Measurements" />,
    },
    {
        Icon: CalendarRange,
        eyebrow: '06 — Programs & Workouts',
        headline: 'Structured plans, built your way',
        body:
            'Organise exercises into workouts, and build week-long programs. Supports metric and imperial — the app adapts to your preferred unit everywhere.',
        bullets: ['Build weekly programs', 'Metric & imperial', 'Reorder anytime'],
        mockup: <PhoneMockup src={ART.analytics} alt="Programs & Workouts" />,
    },
];

const FeatureRow = ({ feature, index }) => {
    const reverse = index % 2 === 1;
    const { Icon } = feature;

    return (
        <div
            data-testid={LANDING.feature(index + 1)}
            className={`grid lg:grid-cols-2 gap-10 lg:gap-20 items-center ${
                reverse ? 'lg:[direction:rtl]' : ''
            }`}
        >
            {/* Copy */}
            <div className="lg:[direction:ltr] max-w-xl">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-[#b8ff00]/10 border border-[#b8ff00]/30 mb-5">
                    <Icon className="w-5 h-5 text-[#b8ff00]" strokeWidth={2.25} />
                </div>
                <div className="text-xs uppercase tracking-[0.22em] text-[#b8ff00] font-semibold mb-3">
                    {feature.eyebrow}
                </div>
                <h3 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight leading-[1.05] mb-5">
                    {feature.headline}
                </h3>
                <p className="text-[#8e8e93] text-base sm:text-lg leading-relaxed mb-6">
                    {feature.body}
                </p>

                <ul className="space-y-2.5">
                    {feature.bullets.map((b) => (
                        <li
                            key={b}
                            className="flex items-center gap-3 text-sm text-white/85"
                        >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#b8ff00] flex-shrink-0" />
                            {b}
                        </li>
                    ))}
                </ul>

                {feature.showTags && (
                    <div className="mt-6 flex flex-wrap gap-1.5">
                        {MUSCLE_TAGS.map((t) => (
                            <span
                                key={t.label}
                                className="tag-pill"
                                style={{ background: t.color }}
                            >
                                {t.label}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* Mockup */}
            <div className="lg:[direction:ltr] relative max-w-[340px] mx-auto w-full">
                <div className="relative">{feature.mockup}</div>
            </div>
        </div>
    );
};

export const Features = () => {
    return (
        <section
            id="features"
            data-testid={LANDING.featuresSection}
            className="relative py-28 sm:py-36"
        >
            <div className="max-w-7xl mx-auto px-5 sm:px-8">
                {/* Section heading */}
                <div className="max-w-3xl mb-24">
                    <div className="text-xs uppercase tracking-[0.25em] text-[#b8ff00] font-semibold mb-4">
                        — Features
                    </div>
                    <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white tracking-tight leading-[1.02]">
                        Everything serious
                        <br />
                        lifters actually need.
                    </h2>
                </div>

                <div className="space-y-32 sm:space-y-40">
                    {features.map((f, i) => (
                        <FeatureRow feature={f} index={i} key={f.headline} />
                    ))}
                </div>
            </div>
        </section>
    );
};

export default Features;
